import { ReactElement, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { PendingBadge } from '~/pages/Analysis/SchedulesModals/DAM/components/ScheduleList/PendingBadge';
import { Card, PrimaryButton } from '../../ds';
import {
  DISPLAY_DAY_ORDER, findScheduleTitle, formatExceptionDate, groupWeekSchedules, isFullDay, listExceptions,
  ProgrammingChangeResult, ProgrammingState, removeException, removeScheduleGroup, saveException,
  saveScheduleGroup, ScheduleGroup,
} from '../../schedules';
import { DamEcoSimplifiedDeviceInfo, DamEcoSimplifiedFormValues } from '../../types';
import { getDayLabelKey } from '../controls';
import { PlusIcon } from '../icons';
import { EMPTY_EXCEPTION_VALUES, ExceptionModal, ExceptionModalValues } from './ExceptionModal';
import { ExceptionsList, ExceptionView } from './ExceptionsList';
import { ScheduleCard } from './ScheduleCard';
import { EMPTY_SCHEDULE_VALUES, ScheduleModal, ScheduleModalValues } from './ScheduleModal';
import {
  CardsGrid, EmptyState, ListHeader, ListHint, Tab, TabBadge, TabsBar, TotalLabel,
} from './styles';

interface SchedulesSectionProps {
  device: DamEcoSimplifiedDeviceInfo;
  isSaving: boolean;
}

type ScheduleModalState = { previous: ScheduleGroup | null; values: ScheduleModalValues };
type ExceptionModalState = { previousDate: string | null; values: ExceptionModalValues };

/**
 * Programações e exceções do DAM, a mesma fonte da tela do modo atual. As edições ficam no formulário
 * e vão no salvamento único; os títulos livres só existem nesta tela.
 */
export function SchedulesSection({ device, isSaving }: Readonly<SchedulesSectionProps>): ReactElement {
  const { t } = useTranslation();
  const { control } = useFormContext<DamEcoSimplifiedFormValues>();
  const { field: programmingField } = useController({ control, name: 'programming' });
  const { field: titlesField } = useController({ control, name: 'programmingTitles' });
  const [tab, setTab] = useState<'schedules' | 'exceptions'>('schedules');
  const [scheduleModal, setScheduleModal] = useState<ScheduleModalState | null>(null);
  const [exceptionModal, setExceptionModal] = useState<ExceptionModalState | null>(null);

  const state: ProgrammingState = { programming: programmingField.value, titles: titlesField.value };
  const groups = groupWeekSchedules(state.programming.week);
  const exceptions: ExceptionView[] = listExceptions(state.programming.exceptions).map((item) => ({
    ...item, title: state.titles.exceptions[item.date] || formatExceptionDate(item.date),
  }));
  const canEdit = device.canManageSchedule;
  const actionsDisabled = isSaving;

  function autoTitle(group: ScheduleGroup): string {
    const days = DISPLAY_DAY_ORDER.filter((day) => group.days.includes(day)).map((day) => t(getDayLabelKey(day)));
    return `${t('programacao')} ${days.join(', ')}`;
  }

  function applyState(next: ProgrammingState): void {
    programmingField.onChange(next.programming);
    titlesField.onChange(next.titles);
  }

  function applyResult(result: ProgrammingChangeResult, conflictKey: string): boolean {
    if (!result.ok) {
      toast.error(t(conflictKey));
      return false;
    }
    applyState(result.state);
    return true;
  }

  function openScheduleModal(group: ScheduleGroup | null): void {
    setScheduleModal({
      previous: group,
      values: group ? {
        title: findScheduleTitle(state.titles, group) ?? '',
        days: group.days,
        start: group.start,
        end: group.end,
        fullDay: isFullDay(group.start, group.end),
        permission: group.permission,
      } : EMPTY_SCHEDULE_VALUES,
    });
  }

  function onSubmitSchedule(values: ScheduleModalValues): void {
    if (!scheduleModal) return;
    const result = saveScheduleGroup(state, scheduleModal.previous, {
      days: values.days, permission: values.permission, start: values.start, end: values.end, title: values.title,
    });
    if (applyResult(result, 'jaExisteProgramacaoDia')) setScheduleModal(null);
  }

  function openExceptionModal(item: ExceptionView | null): void {
    setExceptionModal({
      previousDate: item?.date ?? null,
      values: item ? {
        date: item.date, start: item.start, end: item.end, fullDay: isFullDay(item.start, item.end), permission: item.permission,
      } : EMPTY_EXCEPTION_VALUES,
    });
  }

  function onSubmitException(values: ExceptionModalValues): void {
    if (!exceptionModal) return;
    const result = saveException(state, exceptionModal.previousDate, {
      date: values.date, permission: values.permission, start: values.start, end: values.end,
    });
    if (applyResult(result, 'jaExisteExcecaoData')) setExceptionModal(null);
  }

  return (
    <Card data-testid="eco-simplificado-programacoes">
      <TabsBar role="tablist">
        <Tab type="button" role="tab" aria-selected={tab === 'schedules'} $active={tab === 'schedules'} onClick={() => setTab('schedules')}>
          {t('programacoes')}
          <TabBadge $active={tab === 'schedules'}>{groups.length}</TabBadge>
        </Tab>
        <Tab type="button" role="tab" aria-selected={tab === 'exceptions'} $active={tab === 'exceptions'} onClick={() => setTab('exceptions')}>
          {t('excecoes')}
          <TabBadge $active={tab === 'exceptions'}>{exceptions.length}</TabBadge>
        </Tab>
      </TabsBar>

      {tab === 'schedules' ? (
        <>
          <ListHeader>
            <TotalLabel>{`${t('total')}: ${groups.length}`}</TotalLabel>
            {canEdit ? (
              <PrimaryButton type="button" disabled={actionsDisabled} onClick={() => openScheduleModal(null)}>
                <PlusIcon size={16} strokeWidth={2.4} />
                {t('ecoSimplificadoAdicionarProgramacao')}
              </PrimaryButton>
            ) : null}
          </ListHeader>
          {groups.length ? (
            <CardsGrid>
              {groups.map((group) => (
                <ScheduleCard
                  key={`${group.permission}-${group.start}-${group.end}-${group.days.join(',')}`}
                  group={group}
                  title={findScheduleTitle(state.titles, group) ?? autoTitle(group)}
                  actions={canEdit ? {
                    onEdit: () => openScheduleModal(group),
                    onRemove: () => applyState(removeScheduleGroup(state, group)),
                    disabled: actionsDisabled,
                  } : null}
                />
              ))}
            </CardsGrid>
          ) : (
            <EmptyState>
              <strong>{t('ecoSimplificadoNenhumaProgramacao')}</strong>
              {t('ecoSimplificadoProgramacaoSubtitulo')}
            </EmptyState>
          )}
        </>
      ) : (
        <>
          <ListHeader>
            <TotalLabel>{`${t('total')}: ${exceptions.length}`}</TotalLabel>
            <ListHint>{t('ecoSimplificadoExcecoesSobrescrevem')}</ListHint>
            {canEdit ? (
              <PrimaryButton type="button" disabled={actionsDisabled} onClick={() => openExceptionModal(null)}>
                <PlusIcon size={17} strokeWidth={2.4} />
                {t('ecoSimplificadoAdicionarExcecao')}
              </PrimaryButton>
            ) : null}
          </ListHeader>
          {exceptions.length ? (
            <ExceptionsList
              exceptions={exceptions}
              actions={canEdit ? {
                onEdit: openExceptionModal,
                onRemove: (item) => applyState(removeException(state, item.date)),
                disabled: actionsDisabled,
              } : null}
            />
          ) : (
            <EmptyState>
              <strong>{t('ecoSimplificadoNenhumaExcecao')}</strong>
              {t('ecoSimplificadoExcecoesSobrescrevem')}
            </EmptyState>
          )}
        </>
      )}

      {device.isOnline ? null : <PendingBadge />}

      {scheduleModal ? (
        <ScheduleModal
          initialValues={scheduleModal.values}
          isEdit={!!scheduleModal.previous}
          onClose={() => setScheduleModal(null)}
          onSubmit={onSubmitSchedule}
        />
      ) : null}
      {exceptionModal ? (
        <ExceptionModal
          initialValues={exceptionModal.values}
          isEdit={!!exceptionModal.previousDate}
          onClose={() => setExceptionModal(null)}
          onSubmit={onSubmitException}
        />
      ) : null}
    </Card>
  );
}
